// Modale « Reporter » (WKF-013 §7.4). Réutilise EXACTEMENT les raccourcis
// contextuels de la création de tâche (DueDatePicker) — même logique de report
// pour toute notification (workflow, tâche, native Argalis) — et permet aussi
// une date + heure PRÉCISES (se faire rappeler juste avant un rendez-vous).

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/contexts/AuthContext'
import {
    notificationErrorMessage,
    useNotificationMutations,
} from '@/hooks/useNotifications'
import type { Notification } from '@/hooks/useNotifications.types'

import { canReschedule } from './calendar/taskCalendar.utils'
import { DueDatePicker } from './DueDatePicker'

interface SnoozeDialogProps {
    notification: Notification | null
    entityId: string | null
    onOpenChange: (open: boolean) => void
    onSnoozed?: () => void
}

function tomorrowMorning(): string {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(9, 0, 0, 0)
    return d.toISOString()
}

export function SnoozeDialog({ notification, entityId, onOpenChange, onSnoozed }: SnoozeDialogProps) {
    const { t } = useTranslation()
    const { hasPermission, user } = useAuth()
    const open = notification !== null
    const { snooze, updateTask } = useNotificationMutations(entityId)
    const [until, setUntil] = useState<string | null>(tomorrowMorning())
    // Tâche éditable → on déplace l'échéance (visible partout) ; sinon snooze.
    const reschedule = notification
        ? canReschedule(notification, { userId: user?.id, canUpdate: hasPermission('tasks:update') })
        : false
    const pending = snooze.isPending || updateTask.isPending

    useEffect(() => {
        if (open) setUntil(tomorrowMorning())
    }, [open, notification?.id])

    async function submit() {
        if (!notification || !until) return
        try {
            if (reschedule) {
                await updateTask.mutateAsync({ id: notification.id, payload: { due_date: until } })
            } else {
                await snooze.mutateAsync({ id: notification.id, snoozed_until: until })
            }
            toast.success(t('notifications.snooze.done', 'Notification reportée.'))
            onSnoozed?.()
            onOpenChange(false)
        } catch (err) {
            toast.error(notificationErrorMessage(err, t))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>{t('notifications.snooze.title', 'Reporter la notification')}</DialogTitle>
                </DialogHeader>
                {/* Report précis : juste date + heure (les presets rapides vivent déjà
                    dans le menu « Reporter » qui a ouvert cette modale). */}
                <div className="py-2">
                    <DueDatePicker value={until} onChange={setUntil} allowNoDate={false} hidePresets />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
                        {t('common.cancel', 'Annuler')}
                    </Button>
                    <Button onClick={submit} disabled={pending || !until}>
                        {t('notifications.snooze.confirm', 'Reporter')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
